// ---------------------------------------------------------------------------
// imageOps.js — pure raster helpers used by the editor's image tools.
//
// Extracted from the monolith so the class stays about canvas orchestration,
// not pixel loops. No fabric / DOM-state dependency beyond a scratch <canvas>.
//   - upscaleImageElement: multi-step bicubic-ish upscale + unsharp finish
//   - unsharpMask:        in-place sharpening of an ImageData
//   - inpaint:            BFS boundary-propagation fill (magic erase)
// ---------------------------------------------------------------------------

// Hard ceiling on any generated raster so a stray "8x on a 2560px image" can't
// try to allocate a 20k×20k canvas and freeze/crash the tab.
export const MAX_RASTER_DIM = 8192;

/**
 * Unsharp mask: sharpen an ImageData in place.
 * amount: strength (0-3 typical). radius fixed at 1px for speed.
 */
export function unsharpMask(imageData, w, h, amount = 1.2, passes = 1) {
  const px = imageData.data;
  for (let pass = 0; pass < passes; pass++) {
    const orig = new Uint8ClampedArray(px);
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const i = (y * w + x) * 4;
        for (let c = 0; c < 3; c++) {
          const blur = (
            orig[((y - 1) * w + (x - 1)) * 4 + c] + orig[((y - 1) * w + x) * 4 + c] * 2 + orig[((y - 1) * w + (x + 1)) * 4 + c] +
            orig[(y * w + (x - 1)) * 4 + c] * 2 + orig[i + c] * 4 + orig[(y * w + (x + 1)) * 4 + c] * 2 +
            orig[((y + 1) * w + (x - 1)) * 4 + c] + orig[((y + 1) * w + x) * 4 + c] * 2 + orig[((y + 1) * w + (x + 1)) * 4 + c]
          ) / 16;
          px[i + c] = Math.max(0, Math.min(255, Math.round(orig[i + c] + (orig[i + c] - blur) * amount)));
        }
      }
    }
  }
}

/**
 * Multi-step upscale a loaded image element by `factor`, capped at
 * MAX_RASTER_DIM. Returns a PNG data URL. Doubles each step for smoother
 * enlargement, then applies a light unsharp finish.
 */
export function upscaleImageElement(el, factor) {
  const srcW = el.naturalWidth || el.width;
  const srcH = el.naturalHeight || el.height;
  let dstW = Math.round(srcW * factor);
  let dstH = Math.round(srcH * factor);
  // Respect the raster ceiling (keep aspect).
  const over = Math.max(dstW, dstH) / MAX_RASTER_DIM;
  if (over > 1) { dstW = Math.round(dstW / over); dstH = Math.round(dstH / over); }

  const srcCanvas = document.createElement('canvas');
  srcCanvas.width = srcW; srcCanvas.height = srcH;
  srcCanvas.getContext('2d').drawImage(el, 0, 0, srcW, srcH);

  let current = srcCanvas;
  let curW = srcW, curH = srcH;
  while (curW < dstW || curH < dstH) {
    const nextW = Math.min(curW * 2, dstW);
    const nextH = Math.min(curH * 2, dstH);
    const step = document.createElement('canvas');
    step.width = nextW; step.height = nextH;
    const ctx = step.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(current, 0, 0, nextW, nextH);
    current = step; curW = nextW; curH = nextH;
  }

  const dstCtx = current.getContext('2d');
  const imageData = dstCtx.getImageData(0, 0, dstW, dstH);
  unsharpMask(imageData, dstW, dstH, 1.2, 1);
  dstCtx.putImageData(imageData, 0, 0);
  // Effective factor may have been clamped by the raster ceiling.
  return { url: current.toDataURL('image/png'), scaledW: dstW, scaledH: dstH, srcW, srcH };
}

/**
 * BFS boundary-propagation inpainting. Fills masked pixels layer by layer from
 * the outside in, averaging non-masked neighbours. O(masked_pixels).
 * `maskData`: red channel > 128 marks pixels to fill.
 */
export function inpaint(imageData, maskData) {
  const w = imageData.width, h = imageData.height;
  const px = imageData.data;

  const isMasked = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) isMasked[i] = maskData.data[i * 4] > 128 ? 1 : 0;

  const dilated = new Uint8Array(isMasked);
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      if (isMasked[y * w + x]) {
        for (let dy = -1; dy <= 1; dy++)
          for (let dx = -1; dx <= 1; dx++)
            dilated[(y + dy) * w + (x + dx)] = 1;
      }
    }
  }
  for (let i = 0; i < w * h; i++) isMasked[i] = dilated[i];

  const DX = [-1, 0, 1, -1, 1, -1, 0, 1];
  const DY = [-1, -1, -1, 0, 0, 1, 1, 1];

  let queue = [];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const idx = y * w + x;
      if (!isMasked[idx]) continue;
      for (let d = 0; d < 8; d++) {
        const nx = x + DX[d], ny = y + DY[d];
        if (nx >= 0 && nx < w && ny >= 0 && ny < h && !isMasked[ny * w + nx]) { queue.push(idx); break; }
      }
    }
  }

  let borderAlphaSum = 0, borderAlphaCount = 0;
  for (let q = 0; q < queue.length; q++) {
    const idx = queue[q];
    const x = idx % w, y = (idx / w) | 0;
    for (let d = 0; d < 8; d++) {
      const nx = x + DX[d], ny = y + DY[d];
      if (nx >= 0 && nx < w && ny >= 0 && ny < h) {
        const ni = ny * w + nx;
        if (!isMasked[ni]) { borderAlphaSum += px[ni * 4 + 3]; borderAlphaCount++; }
      }
    }
  }
  const avgBorderAlpha = borderAlphaCount > 0 ? borderAlphaSum / borderAlphaCount : 255;
  const eraseToTransparent = avgBorderAlpha < 128;

  if (eraseToTransparent) {
    for (let i = 0; i < w * h; i++) {
      if (isMasked[i]) { const pi = i * 4; px[pi] = 0; px[pi + 1] = 0; px[pi + 2] = 0; px[pi + 3] = 0; }
    }
  } else {
    while (queue.length > 0) {
      const nextQueue = [];
      for (let q = 0; q < queue.length; q++) {
        const idx = queue[q];
        if (!isMasked[idx]) continue;
        const x = idx % w, y = (idx / w) | 0;
        let r = 0, g = 0, b = 0, a = 0, count = 0;
        for (let d = 0; d < 8; d++) {
          const nx = x + DX[d], ny = y + DY[d];
          if (nx >= 0 && nx < w && ny >= 0 && ny < h) {
            const ni = ny * w + nx;
            if (!isMasked[ni]) { const pi = ni * 4; r += px[pi]; g += px[pi + 1]; b += px[pi + 2]; a += px[pi + 3]; count++; }
          }
        }
        if (count > 0) {
          const pi = idx * 4;
          px[pi] = (r / count) | 0; px[pi + 1] = (g / count) | 0; px[pi + 2] = (b / count) | 0; px[pi + 3] = (a / count) | 0;
          isMasked[idx] = 0;
          for (let d = 0; d < 8; d++) {
            const nx = x + DX[d], ny = y + DY[d];
            if (nx >= 0 && nx < w && ny >= 0 && ny < h && isMasked[ny * w + nx]) nextQueue.push(ny * w + nx);
          }
        }
      }
      queue = nextQueue;
    }
  }

  const wasInpainted = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) wasInpainted[i] = dilated[i];
  const tmpPx = new Uint8ClampedArray(px);
  for (let pass = 0; pass < 3; pass++) {
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        if (!wasInpainted[y * w + x]) continue;
        let r = 0, g = 0, b = 0, a = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const pi = ((y + dy) * w + (x + dx)) * 4;
            r += tmpPx[pi]; g += tmpPx[pi + 1]; b += tmpPx[pi + 2]; a += tmpPx[pi + 3];
          }
        }
        const pi = (y * w + x) * 4;
        px[pi] = (r / 9) | 0; px[pi + 1] = (g / 9) | 0; px[pi + 2] = (b / 9) | 0; px[pi + 3] = (a / 9) | 0;
      }
    }
    tmpPx.set(px);
  }
}
